import React, { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Building2, Check, ChevronDown, Search, X } from 'lucide-react';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from 'components/ui/form';
import { Input } from 'components/ui/input';
import { Button } from 'components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from 'components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { Spinner } from 'components/ui/spinner';
import { getOrganizationDisplayLabel } from 'helpers';
import Locales from 'locales';

const copy = Locales.user.redesign;

export const userInputClassName =
  'h-10 rounded-[7px] border-[#d9e2db] bg-white px-[11px] text-xs text-[#3d434a] placeholder:text-[#5f6875] focus:!outline-none focus-visible:border-hajj-primary focus-visible:ring-2 focus-visible:ring-[rgba(43,123,83,0.10)] focus-visible:ring-offset-0 aria-[invalid=true]:border-[#c77b74] aria-[invalid=true]:focus-visible:border-[#c77b74] aria-[invalid=true]:focus-visible:ring-[rgba(168,62,62,0.08)] disabled:cursor-default disabled:bg-[#f7f9f7] disabled:text-[#6f7b73] disabled:opacity-100';

const normalizeSearchValue = (value) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

export const UserModal = ({
  children,
  description,
  form,
  formId,
  isOpen,
  isSubmitting,
  onClose,
  onSubmit,
  primaryLabel,
  title,
}) => (
  <Dialog
    open={isOpen}
    onOpenChange={(open) => {
      if (!open && !isSubmitting) onClose();
    }}
  >
    <DialogContent
      className="max-h-[calc(100dvh-4rem)] w-[570px] max-w-[calc(100vw-2rem)] gap-0 overflow-hidden rounded-xl border-[#dce3de] bg-white p-0 text-[#3d434a] shadow-[0_12px_40px_rgba(32,48,39,0.10)]"
      onEscapeKeyDown={(event) => {
        if (isSubmitting) event.preventDefault();
      }}
      onPointerDownOutside={(event) => {
        if (isSubmitting) event.preventDefault();
      }}
    >
      <DialogHeader className="px-6 pb-[15px] pt-5 pr-14 text-left">
        <DialogTitle className="text-lg font-semibold leading-6 tracking-[-0.3px] text-[#252a30]">{title}</DialogTitle>
        <DialogDescription className="text-xs leading-[18px] text-[#5f6875]">{description}</DialogDescription>
      </DialogHeader>

      <Form {...form}>
        <form
          id={formId}
          className="flex min-h-0 flex-col overflow-hidden"
          aria-busy={isSubmitting}
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <div className="min-h-0 overflow-y-auto px-6 pb-5">{children}</div>

          <div className="flex flex-none items-center justify-end gap-2 border-t border-[#e8ece9] bg-[#fcfdfc] px-6 py-4">
            <span className="mr-auto text-[10px] text-[#5f6875] max-[420px]:hidden">{copy.required_fields}</span>
            <Button
              type="button"
              variant="outline"
              className="h-[35px] rounded-lg border-[#dce3de] px-3 text-xs shadow-none"
              disabled={isSubmitting}
              onClick={onClose}
            >
              {copy.cancel}
            </Button>
            <Button
              type="submit"
              variant="hajj"
              className="h-[35px] min-w-[124px] rounded-lg px-3 text-xs shadow-none hover:shadow-none"
              disabled={isSubmitting}
            >
              {isSubmitting && <Spinner aria-label={copy.saving} />}
              {isSubmitting ? copy.saving : primaryLabel}
            </Button>
          </div>
        </form>
      </Form>
    </DialogContent>
  </Dialog>
);

UserModal.propTypes = {
  children: PropTypes.node.isRequired,
  description: PropTypes.string.isRequired,
  form: PropTypes.shape({
    handleSubmit: PropTypes.func.isRequired,
  }).isRequired,
  formId: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  primaryLabel: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
};

const RequiredMark = () => (
  <span className="text-[11px] font-normal text-[#79867d]" aria-hidden="true">
    *
  </span>
);

const fieldLabelClassName = 'mb-2 flex items-center gap-1 text-xs font-medium leading-[17px] text-[#3d434a]';
const messageClassName = 'absolute left-0 top-full pt-[3px] text-[11px] font-normal leading-4 text-[#a83e3e]';

const UserTextField = ({ control, label, name, placeholder, type = 'text', autoComplete }) => (
  <FormField
    control={control}
    name={name}
    render={({ field }) => (
      <FormItem className="relative space-y-0">
        <FormLabel className={fieldLabelClassName}>
          {label} <RequiredMark />
        </FormLabel>
        <FormControl>
          <Input
            {...field}
            type={type}
            autoComplete={autoComplete}
            placeholder={placeholder}
            className={userInputClassName}
          />
        </FormControl>
        <FormMessage className={messageClassName} />
      </FormItem>
    )}
  />
);

UserTextField.propTypes = {
  autoComplete: PropTypes.string,
  control: PropTypes.shape({}).isRequired,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  type: PropTypes.string,
};

export const UserIdentityFields = ({ control, emailReadOnly = false, emailValue = '' }) => (
  <>
    <div className="mb-5 grid grid-cols-1 gap-5 min-[421px]:grid-cols-2 min-[421px]:gap-4">
      <UserTextField
        control={control}
        name="firstName"
        label={copy.first_names}
        placeholder={copy.first_names_placeholder}
        autoComplete="given-name"
      />
      <UserTextField
        control={control}
        name="lastName"
        label={copy.columns.name}
        placeholder={copy.last_name_placeholder}
        autoComplete="family-name"
      />
    </div>

    <div className="relative mb-5">
      {emailReadOnly ? (
        <>
          <label htmlFor="edit-user-email" className={fieldLabelClassName}>
            {copy.email_address} <RequiredMark />
          </label>
          <Input
            id="edit-user-email"
            type="email"
            value={emailValue}
            className={userInputClassName}
            readOnly
            disabled
          />
        </>
      ) : (
        <UserTextField
          control={control}
          name="email"
          label={copy.email_address}
          placeholder={copy.email_placeholder}
          type="email"
          autoComplete="email"
        />
      )}
    </div>
  </>
);

UserIdentityFields.propTypes = {
  control: PropTypes.shape({}).isRequired,
  emailReadOnly: PropTypes.bool,
  emailValue: PropTypes.string,
};

export const OrganizationContext = ({ description, organizationName }) => (
  <div>
    <span className={fieldLabelClassName}>{copy.organization}</span>
    <div className="flex items-start gap-2.5 rounded-[7px] bg-[#f6f9f6] px-3.5 py-[13px]">
      <Building2 className="mt-px size-[17px] flex-none text-[#7b9080]" aria-hidden="true" />
      <div>
        <strong className="mb-1 block text-xs font-medium text-[#3d434a]">
          {getOrganizationDisplayLabel(organizationName) || '-'}
        </strong>
        <p className="m-0 text-[11px] leading-[17px] text-[#5f6875]">{description}</p>
      </div>
    </div>
  </div>
);

OrganizationContext.propTypes = {
  description: PropTypes.string.isRequired,
  organizationName: PropTypes.string,
};

const OrganizationSelector = ({ describedBy, disabled, onChange, options, value, invalid }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const searchRef = useRef(null);
  const optionRefs = useRef([]);

  const normalizedOptions = useMemo(
    () =>
      options.map((organization) => ({
        id: organization.id || organization,
        label: getOrganizationDisplayLabel(organization) || organization.name || organization,
      })),
    [options]
  );
  const selectedOptions = normalizedOptions.filter((organization) => value.includes(organization.id));
  const visibleOptions = normalizedOptions.filter((organization) =>
    normalizeSearchValue(organization.label).includes(normalizeSearchValue(search.trim()))
  );

  const toggleOrganization = (organizationId) => {
    if (value.includes(organizationId)) {
      onChange(value.filter((id) => id !== organizationId));
      return;
    }

    onChange([...value, organizationId]);
  };

  const focusOption = (index) => {
    if (visibleOptions.length === 0) return;
    const wrappedIndex = (index + visibleOptions.length) % visibleOptions.length;
    optionRefs.current[wrappedIndex]?.focus();
  };

  const handleOptionKeyDown = (event, index) => {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    if (event.key === 'Home') focusOption(0);
    else if (event.key === 'End') focusOption(visibleOptions.length - 1);
    else focusOption(index + (event.key === 'ArrowDown' ? 1 : -1));
  };

  return (
    <Popover
      modal
      open={isOpen}
      onOpenChange={(open) => {
        if (!disabled) {
          setIsOpen(open);
          if (!open) setSearch('');
        }
      }}
    >
      <div className="relative">
        <PopoverTrigger asChild>
          <button
            id="user-organizations"
            type="button"
            className="flex h-[42px] max-h-[42px] min-h-[42px] w-full items-center gap-2 overflow-hidden rounded-[7px] border border-[#d9e2db] bg-white px-2.5 py-1.5 text-left text-xs text-[#5f6875] outline-none hover:border-[#a6bbaa] focus-visible:border-hajj-primary focus-visible:ring-2 focus-visible:ring-[rgba(43,123,83,0.10)] disabled:cursor-default disabled:opacity-50 aria-[invalid=true]:border-[#c77b74] aria-[invalid=true]:focus-visible:border-[#c77b74] aria-[invalid=true]:focus-visible:ring-[rgba(168,62,62,0.08)] data-[state=open]:border-[#81ab91] data-[state=open]:ring-2 data-[state=open]:ring-[rgba(43,123,83,0.10)]"
            aria-haspopup="listbox"
            aria-controls="user-organization-options"
            aria-describedby={describedBy}
            aria-expanded={isOpen}
            aria-invalid={invalid}
            disabled={disabled}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown') {
                event.preventDefault();
                setIsOpen(true);
              }
            }}
          >
            <span className="flex h-7 min-w-0 flex-1 items-center gap-1 overflow-hidden whitespace-nowrap">
              {selectedOptions.length === 0 && (
                <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                  {copy.organization_select_placeholder}
                </span>
              )}
            </span>
            <ChevronDown className="size-3.5 flex-none text-[#5f6875]" aria-hidden="true" />
          </button>
        </PopoverTrigger>
        {selectedOptions.length > 0 && (
          <span className="pointer-events-none absolute inset-y-0 left-2.5 right-8 z-10 flex min-w-0 items-center gap-1 overflow-hidden whitespace-nowrap">
            {selectedOptions.slice(0, 2).map((organization) => (
              <span
                key={organization.id}
                title={organization.label}
                className="inline-flex h-6 min-w-0 max-w-[calc((100%_-_42px)/2)] flex-[0_1_auto] items-center rounded-[5px] bg-hajj-primary pl-[7px] pr-1 text-[11px] font-medium text-white"
              >
                <span className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">{organization.label}</span>
                <button
                  type="button"
                  className="pointer-events-auto ml-0.5 inline-flex size-4 flex-none items-center justify-center rounded-sm text-white/80 outline-none hover:bg-white/15 hover:text-white focus-visible:ring-2 focus-visible:ring-white/80 disabled:pointer-events-none"
                  aria-label={Locales.formatString(copy.templates.remove_organization, { name: organization.label })}
                  disabled={disabled}
                  onClick={() => toggleOrganization(organization.id)}
                >
                  <X className="size-3" aria-hidden="true" />
                </button>
              </span>
            ))}
            {selectedOptions.length > 2 && (
              <span className="inline-flex h-6 flex-none items-center rounded-[5px] bg-hajj-primary px-[7px] py-[3px] text-[11px] font-medium text-white">
                +{selectedOptions.length - 2}
              </span>
            )}
          </span>
        )}
      </div>

      <PopoverContent
        side="bottom"
        align="start"
        sideOffset={6}
        avoidCollisions={false}
        className="z-[60] flex h-[220px] w-[var(--radix-popover-trigger-width)] flex-col overflow-hidden rounded-lg border-[#d5ded7] bg-white p-0 shadow-[0_6px_20px_rgba(32,57,39,0.10)]"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          searchRef.current?.focus();
        }}
      >
        <div className="relative flex-none border-b border-[#e9edea] px-2.5 py-[9px]">
          <Search
            className="pointer-events-none absolute left-5 top-[19px] size-3.5 text-[#5f6875]"
            aria-hidden="true"
          />
          <Input
            ref={searchRef}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                focusOption(event.key === 'ArrowDown' ? 0 : visibleOptions.length - 1);
              }
            }}
            placeholder={copy.organization_search_placeholder}
            aria-label={copy.organization_search_label}
            aria-controls="user-organization-options"
            aria-expanded={isOpen}
            aria-autocomplete="list"
            role="combobox"
            autoComplete="off"
            className="h-8 rounded-[5px] border-[#e4e9e5] bg-[#fafcfb] py-0 pl-[30px] pr-2.5 text-[11px] text-[#3d434a] placeholder:text-[#5f6875] focus:!outline-none focus-visible:border-hajj-primary focus-visible:ring-2 focus-visible:ring-[rgba(43,123,83,0.10)] focus-visible:ring-offset-0"
          />
        </div>

        <div
          id="user-organization-options"
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-[5px]"
          role="listbox"
          aria-label={copy.organization_options_label}
          aria-multiselectable="true"
        >
          {visibleOptions.map((organization, index) => {
            const isSelected = value.includes(organization.id);

            return (
              <button
                key={organization.id}
                ref={(node) => {
                  optionRefs.current[index] = node;
                }}
                type="button"
                role="option"
                aria-selected={isSelected}
                className="mb-1 flex min-h-8 w-full items-center justify-between gap-2 rounded-[5px] border border-[#e8ede9] bg-white px-[9px] text-left text-[11px] text-[#3d434a] outline-none last:mb-0 hover:border-[#9cbcaa] hover:bg-[#f7faf8] focus-visible:ring-2 focus-visible:ring-[#78a488] aria-selected:border-hajj-primary aria-selected:bg-hajj-primary aria-selected:text-white aria-selected:hover:bg-[#216341]"
                onClick={() => toggleOrganization(organization.id)}
                onKeyDown={(event) => handleOptionKeyDown(event, index)}
              >
                <span>{organization.label}</span>
                {isSelected && <Check className="size-[13px]" aria-hidden="true" />}
              </button>
            );
          })}

          {visibleOptions.length === 0 && (
            <p className="m-2.5 text-center text-[11px] text-[#5f6875]">{copy.organization_empty}</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

OrganizationSelector.propTypes = {
  describedBy: PropTypes.string,
  disabled: PropTypes.bool.isRequired,
  invalid: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})])).isRequired,
  value: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export const OrganizationField = ({ control, disabled = false, organizations }) => (
  <FormField
    control={control}
    name="organizationIds"
    render={({ field, fieldState }) => (
      <FormItem className="space-y-0">
        <FormLabel className={fieldLabelClassName} htmlFor="user-organizations">
          {copy.organizations} <RequiredMark />
        </FormLabel>
        <OrganizationSelector
          describedBy={fieldState.error ? 'user-organizations-error' : undefined}
          disabled={disabled}
          invalid={!!fieldState.error}
          onChange={field.onChange}
          options={organizations}
          value={field.value || []}
        />
        <div className="min-h-6 pt-[7px]" aria-live="polite">
          <FormMessage
            id="user-organizations-error"
            className="text-[11px] font-normal leading-[17px] text-[#a83e3e]"
          />
        </div>
      </FormItem>
    )}
  />
);

OrganizationField.propTypes = {
  control: PropTypes.shape({}).isRequired,
  disabled: PropTypes.bool,
  organizations: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})])).isRequired,
};
